import { ORDER_STATUSES, money, statusBadge } from '../utils/order';
import React, { useEffect, useRef, useState } from 'react';

import { Link } from 'react-router-dom';
import OrderService from '../services/order.service';
import Pagination from './Pagination';
import Spinner from './Spinner';
import toast from 'react-hot-toast';
import useAutoRefresh from '../common/http/useAutoRefresh';
import { useTranslation } from 'react-i18next';

const OrderManagement = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState({ status: '', q: '' });
  const PAGE_SIZE = 10;

  // Statuses that can be hidden from the list (remembered in localStorage as
  // orders.hide<Status>). An explicit status filter still shows them.
  const HIDEABLE = ['cancelled', 'paid', 'shipped', 'completed'];
  const hideKey = (s) => `orders.hide${s.charAt(0).toUpperCase()}${s.slice(1)}`;
  const [hidden, setHidden] = useState(() => HIDEABLE.filter((s) => localStorage.getItem(hideKey(s)) === '1'));
  const firstHiddenRun = useRef(true);
  const toggleHidden = (s, checked) => {
    localStorage.setItem(hideKey(s), checked ? '1' : '0');
    setHidden((h) => (checked ? [...h.filter((x) => x !== s), s] : h.filter((x) => x !== s)));
  };

  const resolveMsg = (err) => (err.response && err.response.data && err.response.data.message) || err.message;

  // Each request gets a sequence number; only the latest one may update the
  // table, so a slow background refresh can't overwrite newer results.
  const reqSeq = useRef(0);
  // Manual "Refresh" button: reloads in place (spinner in the button only).
  const [refreshing, setRefreshing] = useState(false);

  // silent: background auto-refresh — no spinner, no error toast.
  // onDone: called after the request settles (only if still the latest one).
  const load = (p = 1, filters = applied, silent = false, onDone) => {
    const seq = ++reqSeq.current;
    if (!silent) setLoading(true);
    const params = { page: p, size: PAGE_SIZE };
    if (filters.status) params.status = filters.status;
    if (filters.q) params.q = filters.q;
    hidden.forEach((s) => (params[`hide_${s}`] = 1));
    OrderService.getAll(params, { silent })
      .then((res) => {
        if (seq !== reqSeq.current) return;
        setOrders(res.data.items);
        setTotalPages(res.data.totalPages || 1);
        setTotalItems(res.data.totalItems || 0);
        setPage(res.data.currentPage || p);
      })
      .catch((err) => {
        if ((!silent || onDone) && seq === reqSeq.current) toast.error(resolveMsg(err));
      })
      .finally(() => {
        if (seq === reqSeq.current) setLoading(false);
        if (onDone) onDone();
      });
  };

  const refresh = () => {
    setRefreshing(true);
    // Errors from a manual refresh are shown (silent only hides the loaders).
    load(page, applied, true, () => setRefreshing(false));
  };

  // Auto refresh the current page every 5s (skipped while a load is running).
  useAutoRefresh(() => {
    if (!loading && !refreshing) load(page, applied, true);
  }, 5000);

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload from page 1 when the hidden statuses change (skip the initial mount).
  useEffect(() => {
    if (firstHiddenRun.current) {
      firstHiddenRun.current = false;
      return;
    }
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden]);

  const applyFilters = (e) => {
    if (e) e.preventDefault();
    const next = { status: statusFilter, q: search.trim() };
    setApplied(next);
    load(1, next);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setSearch('');
    const next = { status: '', q: '' };
    setApplied(next);
    load(1, next);
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    load(p);
  };

  const changeStatus = (order, status) => {
    if (status === order.status) return;
    OrderService.updateStatus(order.id, status)
      .then((res) => {
        // Drop it from view when its new status is currently hidden.
        const hideNow = hidden.includes(status) && applied.status !== status;
        setOrders((list) => (hideNow ? list.filter((o) => o.id !== order.id) : list.map((o) => (o.id === order.id ? res.data : o))));
        toast.success(t('orderManagement.statusChanged', { id: order.id, status: t(`orderStatus.${status}`, status) }));
      })
      .catch((err) => toast.error(resolveMsg(err)));
  };

  return (
    <div className='container mt-4'>
      <h3 className='mb-3'>
        {t('orderManagement.title')} {totalItems > 0 && <small className='text-muted'>{t('orderManagement.count', { count: totalItems })}</small>}
      </h3>

      <form className='row g-2 mb-3' onSubmit={applyFilters}>
        <div className='col-12 col-sm-3'>
          <select className='form-select' value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value=''>{t('orderManagement.allStatuses')}</option>
            {ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`orderStatus.${s}`, s)}
              </option>
            ))}
          </select>
        </div>
        <div className='col-12 col-sm-5'>
          <input type='text' className='form-control' placeholder={t('orderManagement.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className='col-12 col-sm-4 d-flex gap-2'>
          <button type='submit' className='btn btn-outline-primary flex-grow-1'>
            {t('orderManagement.filter')}
          </button>
          {(applied.status || applied.q) && (
            <button type='button' className='btn btn-outline-secondary' onClick={clearFilters}>
              {t('orderManagement.clear')}
            </button>
          )}
          <button type='button' className='btn btn-outline-secondary' onClick={refresh} disabled={loading || refreshing} title={t('common.refresh')}>
              {refreshing ? <span className='spinner-border spinner-border-sm me-1' aria-hidden='true' /> : <span className='me-1' aria-hidden='true'>↻</span>}
              {t('common.refresh')}
            </button>
        </div>
      </form>

      <div className='d-flex flex-wrap gap-3'>
        {HIDEABLE.map((s) => (
          <div className='form-check mb-2' key={s}>
            <input className='form-check-input' type='checkbox' id={`hide-${s}`} checked={hidden.includes(s)} onChange={(e) => toggleHidden(s, e.target.checked)} />
            <label className='form-check-label' htmlFor={`hide-${s}`}>
              {t(`orderManagement.hide${s.charAt(0).toUpperCase()}${s.slice(1)}`)}
            </label>
          </div>
        ))}
      </div>

      {loading ? (
        <Spinner />
      ) : orders.length === 0 ? (
        <p className='text-muted'>{t('orderManagement.noOrders')}</p>
      ) : (
        <div className='table-responsive rounded mt-4'>
          <table className='table table-hover align-middle'>
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>{t('orderManagement.date')}</th>
                <th>{t('orderManagement.customer')}</th>
                <th>{t('orderManagement.phone')}</th>
                <th style={{ width: 110 }}>{t('orderManagement.total')}</th>
                <th style={{ width: 160 }}>{t('orderManagement.status')}</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const meta = statusBadge(o.status);
                return (
                  <tr key={o.id}>
                    <td>{o.id}</td>
                    <td>{new Date(o.createdAt).toLocaleDateString()}</td>
                    <td>{o.customer_name}</td>
                    <td>{o.phone}</td>
                    <td className='fw-bold'>{money(o.total_amount)}</td>
                    <td>
                      <span className={`badge ${meta.cls} me-2`}>{t(`orderStatus.${o.status}`, meta.label)}</span>
                      <select className='form-select form-select-sm mt-1' value={o.status} onChange={(e) => changeStatus(o, e.target.value)}>
                        {ORDER_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {t(`orderStatus.${s}`, s)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <Link to={`/orders/${o.id}`} className='btn btn-sm btn-outline-primary'>
                        {t('orderManagement.view')}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && <Pagination page={page} totalPages={totalPages} onChange={goToPage} />}
    </div>
  );
};

export default OrderManagement;
